// File: scholarship-frontend/src/pages/finance/FinanceDashboard.tsx

import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "@/utils/axiosInstance";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import DateRangePicker from "@/components/DateRangePicker";
import {
  awaitingDisbursalLabel,
  financeReviewCompleteLabel,
  paymentCompletedLabel,
} from "@/utils/semanticLabels";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Title,
  Legend,
  Filler, // ✅ required for `fill: true`
} from "chart.js";
import { saveAs } from "file-saver";

// ✅ Register plugins (Filler must be registered for `fill: true`)
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

type PolicyScope = "lifetime" | "academic_year";

interface TenantPolicy {
  allowMultipleDisbursals: boolean;
  policyScope: PolicyScope | string;
  maxCount: number | null;
  maxAmount: number | null;
}

interface PolicyUsageByYear {
  academicYear: number;
  disbursalCountUsed: number;
  disbursedAmountUsed: number;
  maxCount: number | null;
  maxAmount: number | null;
}

interface PolicyUsage {
  scope: PolicyScope | string;
  maxCount: number | null;
  maxAmount: number | null;
  disbursalCountUsed: number | null;
  disbursedAmountUsed: number | null;
  byAcademicYear: PolicyUsageByYear[];
}

interface FinanceTrendPoint {
  date: string;
  count: number;
}

interface FinanceDashboardSummaryResponse {
  pendingDisbursals: number;
  totalDisbursedThisMonth: number;
  upcomingPayments: number;
  trendView: FinanceTrendPoint[];

  tenantPolicy: TenantPolicy;
  policyUsage: PolicyUsage;
}

const StatCard = ({
  label,
  value,
  color,
  currency = false,
}: {
  label: string;
  value: number;
  color: string;
  currency?: boolean;
}) => (
  <Card className="shadow-sm">
    <CardHeader>
      <CardTitle className="text-sm text-muted-foreground">{label}</CardTitle>
    </CardHeader>
    <CardContent>
      <p className={`text-3xl font-bold ${color}`}>
        {currency ? `₹${value.toLocaleString("en-IN")}` : value}
      </p>
    </CardContent>
  </Card>
);

const formatScope = (scope?: string) => {
  if (!scope) return "—";
  if (scope === "academic_year") return "Academic year";
  if (scope === "lifetime") return "Lifetime";
  return scope;
};

const formatCap = (v: number | null | undefined, currency = false) => {
  if (v === null || v === undefined) return "—";
  return currency ? `₹${Number(v).toLocaleString("en-IN")}` : String(v);
};

const FinanceDashboard: React.FC = () => {
  const [summary, setSummary] = useState<FinanceDashboardSummaryResponse | null>(null);
  const [error, setError] = useState<string>("");
  const [view, setView] = useState<"summary" | "table">("summary");
  const [dateRange, setDateRange] = useState<{ startDate: Date; endDate: Date }>({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    endDate: new Date(),
  });

  const fetchSummary = async () => {
    try {
      setSummary(null);
      setError("");

      const params = new URLSearchParams({
        start_date: format(dateRange.startDate, "yyyy-MM-dd"),
        end_date: format(dateRange.endDate, "yyyy-MM-dd"),
      });

      const res = await axiosInstance.get(`/finance/dashboard-summary?${params.toString()}`);
      setSummary(res.data);
    } catch (err) {
      console.error(err);
      setError("Failed to load finance summary.");
    }
  };

  useEffect(() => {
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.startDate, dateRange.endDate]);

  const exportCSV = () => {
    if (!summary?.policyUsage) return;

    const { tenantPolicy, policyUsage } = summary;

    const rows: string[][] = [
      ["Scope", String(tenantPolicy?.policyScope ?? "")],
      ["Allow Multiple Payments", String(tenantPolicy?.allowMultipleDisbursals ?? "")],
      ["Max Count", tenantPolicy?.maxCount == null ? "" : String(tenantPolicy.maxCount)],
      ["Max Amount", tenantPolicy?.maxAmount == null ? "" : String(tenantPolicy.maxAmount)],
      [],
      ["Payment Limit Usage (by academic year)"],
      ["Academic Year", "Count Used", "Amount Used", "Max Count", "Max Amount"],
      ...(policyUsage.byAcademicYear ?? []).map((y) => [
        String(y.academicYear),
        String(y.disbursalCountUsed),
        String(y.disbursedAmountUsed),
        y.maxCount == null ? "" : String(y.maxCount),
        y.maxAmount == null ? "" : String(y.maxAmount),
      ]),
    ];

    const csv = rows.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    saveAs(blob, "finance-policy-usage.csv");
  };

  const chartData = useMemo(() => {
    return {
      labels: summary?.trendView.map((d) => d.date) ?? [],
      datasets: [
        {
          label: financeReviewCompleteLabel(),
          data: summary?.trendView.map((d) => d.count) ?? [],
          fill: true, // ✅ now works because Filler is registered
          borderColor: "#16a34a",
          backgroundColor: "rgba(34,197,94,0.2)",
          tension: 0.4,
        },
      ],
    };
  }, [summary]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Finance Disbursal Dashboard</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setView("summary")}>
            Summary View
          </Button>
          <Button variant="outline" onClick={() => setView("table")}>
            Payment Limits
          </Button>
          <Button variant="secondary" onClick={exportCSV} disabled={!summary}>
            Export CSV
          </Button>
        </div>
      </div>

      <DateRangePicker value={dateRange} onChange={setDateRange} />

      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4 text-sm text-blue-950">
          <div className="font-semibold">What should I do next?</div>
          <p className="mt-1">
            {summary
              ? summary.pendingDisbursals > 0
                ? "Review finance-complete cases waiting for disbursal."
                : summary.upcomingPayments > 0
                  ? "Check finance-flagged cases before disbursal."
                  : "No disbursal is waiting right now."
              : "Load the payment summary."}
          </p>
        </CardContent>
      </Card>

      {error && <p className="text-red-600">{error}</p>}

      {!summary && !error && (
        <div className="grid md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, idx) => (
            <Skeleton key={idx} className="h-[140px] w-full rounded-xl" />
          ))}
        </div>
      )}

      {summary && view === "summary" && (
        <>
          <div className="grid md:grid-cols-3 gap-4">
            <StatCard label={awaitingDisbursalLabel()} value={summary.pendingDisbursals} color="text-gray-900" />
            <StatCard
              label={`${paymentCompletedLabel()} This Month`}
              value={Number(summary.totalDisbursedThisMonth)}
              color="text-green-600"
              currency
            />
            <StatCard label="Finance-Flagged Cases" value={summary.upcomingPayments} color="text-indigo-600" />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle className="text-sm text-muted-foreground">Payment Rules</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Scope</span>
                  <span className="font-medium">{formatScope(summary.tenantPolicy?.policyScope)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Allow multiple payments</span>
                  <span className="font-medium">{summary.tenantPolicy?.allowMultipleDisbursals ? "Yes" : "No"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Max count</span>
                  <span className="font-medium">{formatCap(summary.tenantPolicy?.maxCount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Max amount</span>
                  <span className="font-medium">{formatCap(summary.tenantPolicy?.maxAmount, true)}</span>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle className="text-sm text-muted-foreground">Payment Limit Usage</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Scope</span>
                  <span className="font-medium">{formatScope(summary.policyUsage?.scope)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Count used</span>
                  <span className="font-medium">{summary.policyUsage?.disbursalCountUsed ?? "—"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Amount used</span>
                  <span className="font-medium">
                    {summary.policyUsage?.disbursedAmountUsed == null
                      ? "—"
                      : `₹${Number(summary.policyUsage.disbursedAmountUsed).toLocaleString("en-IN")}`}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Max count</span>
                  <span className="font-medium">{formatCap(summary.policyUsage?.maxCount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Max amount</span>
                  <span className="font-medium">{formatCap(summary.policyUsage?.maxAmount, true)}</span>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="mt-6">
            <h2 className="text-lg font-semibold mb-2">Last 7 Days Finance Review Completion Trend</h2>
            <div className="bg-white border rounded-xl p-4">
              <Line data={chartData} options={{ responsive: true, plugins: { legend: { display: false } } }} />
            </div>
          </div>
        </>
      )}

      {summary && view === "table" && (
        <div className="bg-white rounded-xl border shadow-sm mt-6">
          <div className="p-4 border-b">
            <h2 className="text-lg font-semibold">Payment Limit Usage by Academic Year</h2>
            <p className="text-sm text-muted-foreground">Shows payment usage aggregated per academic year when available.</p>
          </div>

          <table className="w-full text-sm text-left">
            <thead className="bg-gray-100 border-b">
              <tr>
                <th className="p-3 font-semibold text-gray-600">Academic Year</th>
                <th className="p-3 font-semibold text-gray-600">Count Used</th>
                <th className="p-3 font-semibold text-gray-600">Amount Used</th>
                <th className="p-3 font-semibold text-gray-600">Max Count</th>
                <th className="p-3 font-semibold text-gray-600">Max Amount</th>
              </tr>
            </thead>
            <tbody>
              {(summary.policyUsage?.byAcademicYear ?? []).map((entry) => (
                <tr key={entry.academicYear} className="border-b">
                  <td className="p-3">{entry.academicYear}</td>
                  <td className="p-3">{entry.disbursalCountUsed}</td>
                  <td className="p-3">₹{Number(entry.disbursedAmountUsed).toLocaleString("en-IN")}</td>
                  <td className="p-3">{entry.maxCount ?? "—"}</td>
                  <td className="p-3">{entry.maxAmount == null ? "—" : `₹${Number(entry.maxAmount).toLocaleString("en-IN")}`}</td>
                </tr>
              ))}

              {(summary.policyUsage?.byAcademicYear ?? []).length === 0 && (
                <tr>
                  <td className="p-3 text-muted-foreground" colSpan={5}>
                    No payments found for an academic year yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FinanceDashboard;
